<script setup lang="ts">
import { computed, onMounted, watch } from 'vue'
import { IDECONSTANT } from '@/utils/ide'
import { IDEVIEWTABS_MOBILE } from '@/utils/tabs'
import { useIdeStore } from '@/stores/ide.store'
import editorService from '@/services/ide/editor.service'
import blocklyService from '@/services/ide/languages/blockly/blockly.service'
import ExecuteComp from '@/components/ide/ide/ExecuteComp.vue'
import JdroidNotificationModel from '@/components/ide/ide/ai/jdroid/JdroidNotificationModel.vue'
import AdvanceIdeActiveTab from '@/components/ide/ide/AdvanceIdeActiveTab.vue'
import ExecuteCompAdvCompiler from '@/components/ide/ide/ExecuteCompAdvCompiler.vue'
import arrowMinimize from '@/assets/images/ide/arrows-diagonal-invert.svg'
import minimizeIcon from '@/assets/images/ide/arrows-minimize.svg'
import CollapseSettingBarIcon from '@/assets/images/ide/collapseSettingBarMobile.svg?component'

const ideStore = useIdeStore()

const isBlockly = computed(() => {
  return ideStore.isBlockly
})
const isAdvanced = computed(() => {
  return ideStore.isAdvanced
})
const isCodeEditor = computed(() => {
  return ideStore.codeEditor
})
const isFullScreen = computed(() => {
  return ideStore.isFullScreen
})

const projectTreeCollapsed = computed(() => {
  return ideStore.projectTreeCollapsed
})

const isIdeFullScreen = computed(() => {
  return ideStore.ideFullScreen
})

const showSettingSideBarMobile = computed(() => {
  return ideStore.showSettingSideBarMobile
})

const ideMobile = computed(() => {
  return ideStore.currentMobileTab == IDEVIEWTABS_MOBILE.IDE
})

/**
 * handle minimize
 */
const handleMinimize = async () => {
  ideStore.ideFullScreen = false
  ideStore.setIdeFullScreenMaximizedState(false)
  await new Promise((resolve) => setTimeout(resolve, 10))
  editorService.resizeOutputEditor()
}
/**
 * handle open project tree
 */
const handleOpenProjectTree = () => {
  ideStore.setProjectTreeCollapsed(false)
}
/**
 * toggle showSettingSideBarMobile value
 */
const toggleSettingBar = () => {
  ideStore.setShowSettingSideBarMobile(!ideStore.showSettingSideBarMobile)
}

onMounted(() => {
  const tempSize = Number(localStorage.getItem(IDECONSTANT.LOCALSTORAGE_FONT_SIZE_NAME))
  if (tempSize < 11 || tempSize > 25) {
    localStorage.setItem(IDECONSTANT.LOCALSTORAGE_FONT_SIZE_NAME, '12')
    editorService.setFontSize(12)
  }
  watch(isFullScreen, () => {
    editorService.resizeCodeEditor()
    editorService.resizeOutputEditor()
    blocklyService.resizeBlockly()
  })
})
</script>

<template>
  <div :class="['flex', 'w-full', 'h-auto', 'flex-col', 'items-stretch', 'relative']">
    <JdroidNotificationModel />
    <div
      :class="[
        'w-full',
        'h-full',
        'flex',
        'z-1 absolute left-0 top-0',

        {
          'flex-col': isBlockly
        }
      ]"
    >
      <!-- Project Tree Component -->

      <!-- Blockly Editor -->
      <div
        v-if="isBlockly"
        :id="IDECONSTANT.BLOCKLY_EDITOR"
        :class="[
          'z-0 w-full text-black lg:min-w-fit print:border print:p-1',
          {
            'h-full min-h-[288px]': !isFullScreen
          },
          {
            'h-72 lg:h-full': isFullScreen
          }
        ]"
      ></div>

      <!-- Code Editor -->
      <div class="h-full w-full">
        <div v-if="ideMobile || isAdvanced" class="background-quaternary flex h-10 w-full flex-row">
          <div
            v-if="!showSettingSideBarMobile && ideMobile"
            class="flex w-10 items-center justify-center pr-[1px]"
          >
            <button type="button" class="hs-tooltip-toggle h-5 w-5" @click="toggleSettingBar">
              <CollapseSettingBarIcon class="icon-ide-emphasized" />
            </button>
          </div>
          <div v-if="isAdvanced">
            <AdvanceIdeActiveTab />
          </div>
        </div>
        <div v-if="projectTreeCollapsed && isIdeFullScreen && isAdvanced">
          <button @click="handleOpenProjectTree" class="absolute bottom-2 left-2 z-10 h-6 w-6">
            <img :src="minimizeIcon" />
          </button>
        </div>
        <div
          v-show="isCodeEditor"
          id="ideCodeEditor"
          :class="isAdvanced || ideMobile ? 'h-[calc(100%-2.5rem)]' : 'h-full'"
          class="w-full print:border print:p-1"
        >
          <div :id="IDECONSTANT.CODE_EDITOR" class="block min-w-full xl:overflow-auto" />
          <div
            class="absolute right-2 flex justify-center gap-4"
            :class="isIdeFullScreen ? 'top-0.5' : 'top-2'"
          >
            <div v-if="isIdeFullScreen" class="hidden sm:block">
              <div v-if="!ideStore.isAdvancedCompiler">
                <ExecuteComp />
              </div>
              <div v-else>
                <ExecuteCompAdvCompiler />
              </div>
            </div>
            <div v-if="ideStore.ideFullScreen" class="flex">
              <button @click="handleMinimize">
                <img :src="arrowMinimize" class="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
